import { useCallback } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { array, bool, func, string } from 'prop-types'
import { Button, ButtonGroup } from 'react-bootstrap'

import mode from './map-mode'
import { getButtonGroupButtonVariant } from '../../util'

import s from './location.module.scss'

const Location = props => {
  const { hasSavedLocations, mapMode, setMapMode, disabled = false, hideSavedLocations = false, entityLabel = "discrepancy", useExternalSMSDescription = false, keyStatuses = [] } = props

  const changeMapMode = useCallback(
    (mode, target) => {
      if (mode !== mapMode) {
        setMapMode(mode) // only set the map mode state if the map mode has changed
      }

      if (target) {
        // we blur to avoid focus styling
        target.blur()
      }
    },
    [mapMode, setMapMode],
  )

  return (
    <>
      <h5>
        Location
        {keyStatuses.length > 0 && (
          <>
            <span className={s['location-sub-text']}> - mark {entityLabel} on the map</span>
            <OverlayTrigger
              placement={"top"}
              trigger={["hover", "focus", "click"]}
              overlay={
                <Tooltip
                  id="map-key-tooltip"
                  className="tooltip-auto-width"
                >
                  <div className="text-left">
                    {keyStatuses.map((status, idx) => (
                      <div key={idx}>
                        {status.colors.map((color, colorIdx) => (
                          <span
                            key={colorIdx}
                            className={s['location-key-color-block']}
                            title={color.label}
                            style={{ backgroundColor: color.value || "#ccc" }}
                          />
                        ))}
                        <span>= {status.label}</span>
                      </div>
                    ))}
                  </div>
                </Tooltip>
              }
            >
              <span
                tabIndex={0}
                className={s['location-key-legend']}
                aria-label="Show map key"
              >
                Map Key
              </span>
            </OverlayTrigger>
          </>
        )}
      </h5>
      {keyStatuses.length === 0 && (
        <div className={useExternalSMSDescription ? "d-none d-lg-block" : ""}>Mark {entityLabel} on the map</div>
      )}
      {/* Message for smaller screen sizes when useExternalSMSDescription is true*/}
      {keyStatuses.length === 0 && useExternalSMSDescription && (
        <div className="d-block d-lg-none">
          Select an action type, then click "Select a Location" to mark {entityLabel} on map
        </div>
      )}
      <div className="d-flex align-items-baseline mt-2">
        <ButtonGroup>
          <Button
            variant={`${getButtonGroupButtonVariant(mapMode === mode.MARKER)}`}
            onClick={x => changeMapMode(mode.MARKER, x.target)}
            key={mode.MARKER}
            disabled={disabled}
          >
            Pin Location
          </Button>
          <Button
            variant={`${getButtonGroupButtonVariant(mapMode === mode.POLYGON)}`}
            onClick={x => changeMapMode(mode.POLYGON, x.target)}
            key={mode.POLYGON}
            disabled={disabled}
          >
            Select Area
          </Button>
          {!hideSavedLocations && (
            <Button
              variant={`${getButtonGroupButtonVariant(mapMode === mode.SAVED_LOCATION)}`}
              disabled={!hasSavedLocations || disabled}
              onClick={x => changeMapMode(mode.SAVED_LOCATION, x.target)}
              key={mode.SAVED_LOCATION}
            >
              Saved Location
            </Button>
          )}
          <Button
            variant={getButtonGroupButtonVariant(mapMode === mode.CLEAR)}
            onClick={x => changeMapMode(mode.CLEAR, x.target)}
            key={mode.CLEAR}
            disabled={disabled}
          >
            Clear Map
          </Button>
        </ButtonGroup>
      </div>
    </>
  )
}

Location.defaultProps = {
  hasSavedLocations: false,
  hideSavedLocations: false,
  disabled: false,
  mapMode: mode.MARKER,
  setMapMode: () => {},
  displayOptionalIndicator: false,
  entityLabel: "discrepancy",
  useExternalSMSDescription: false,
  keyStatuses: [],
}

Location.propTypes = {
  hasSavedLocations: bool,
  hideSavedLocations: bool,
  disabled: bool,
  mapMode: string,
  setMapMode: func,
  displayOptionalIndicator: bool,
  entityLabel: string,
  useExternalSMSDescription: bool,
  keyStatuses: array,
}

export default Location
